/**
 * Legacy SubType Normalizer
 *
 * Maps legacy offset_mapping entries (from pre-schema CSV imports) to the
 * canonical MessageContent-based column keys used by AdapterEditor's keyed
 * offset state.  Also provides `dedupeUnresolvedEntries` and `logUnresolvedBatch`
 * for the Alert UI introduced in Ticket 4.
 */
import { devDebug, devWarn } from "../../../utils/devLog";
import type { AdapterColumnDescriptor } from "./adapterColumnTypes";

// ─── Constants ──────────────────────────────────────────────────────────────

/** Maps legacy numeric message_type values to their string category keys. */
export const LEGACY_OFFSET_MT_TO_KEY: Record<number, string> = {
  0: "control",
  2: "fault",
  4: "group_supervisory_status",
  5: "service",
  6: "status",
  7: "traffic",
  8: "usage",
};

/** Maps category string keys to their canonical MessageContent numeric values. */
export const CATEGORY_KEY_TO_MC: Record<string, number> = {
  status: 1,
  traffic: 2,
  usage: 3,
  service: 4,
  control: 5,
  fault: 6,
  group_supervisory_status: 7,
  group_control: 8,
  controller_info: 9,
};

// ─── Types ───────────────────────────────────────────────────────────────────

/** A single entry from the server's legacy offset_mapping array. */
export interface LegacyOffsetEntry {
  offset: number;
  unit_number: number;
  message_type: number;
  message_value?: number;
  message_sub_type?: string;
}

/** Result returned by matchLegacyEntryToColumn. */
export interface LegacyMatchResult {
  columnKey: string | null;
  confidence: "exact" | "normalized" | "none";
}

/** One deduplicated group of unresolved legacy entries, used by the Alert UI. */
export interface UnresolvedGroup {
  message_type: number;
  message_sub_type?: string;
  unitSummary: string;
  count: number;
  sampleOffsets: number[];
}

// ─── Internal helpers ────────────────────────────────────────────────────────

/** Returns the category string key for a canonical MC number, or "" if unknown. */
function categoryKeyFromMcNumber(mc: number): string {
  const found = Object.entries(CATEGORY_KEY_TO_MC).find(([, v]) => v === mc);
  return found ? found[0] : "";
}

// ─── Public functions ────────────────────────────────────────────────────────

/**
 * Normalises a legacy sub_type string to a lowercase, space-separated label
 * suitable for fuzzy matching against column labels.
 *
 * Steps (in order):
 *  1. Strip parenthetical annotations: "Heartbeat (primary)" → "Heartbeat"
 *  2. Split camelCase words
 *  3. Collapse non-alphanumeric sequences to spaces
 *  4. Trim and lowercase
 */
export function normalizeSubType(raw: string | null | undefined): string {
  if (raw === null || raw === undefined) return "";
  let s = raw.trim();
  if (s === "") return "";
  s = s.replace(/\s*\([^)]*\)/g, "").trim();
  if (s === "") return "";
  s = s.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  s = s.replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
  s = s.replace(/[^a-zA-Z0-9]+/g, " ");
  s = s.replace(/\s+/g, " ").trim();
  return s.toLowerCase();
}

/**
 * Converts a legacy numeric message_type to its canonical MessageContent
 * numeric value via the two-step LEGACY_OFFSET_MT_TO_KEY → CATEGORY_KEY_TO_MC
 * lookup tables.  Returns 0 (NONE) when the legacy value is unknown.
 */
export function remapLegacyMessageType(legacyMt: number): number {
  const catKey = LEGACY_OFFSET_MT_TO_KEY[legacyMt];
  if (catKey === undefined) return 0;
  return CATEGORY_KEY_TO_MC[catKey] ?? 0;
}

/**
 * Attempts to match a legacy entry against the adapter's column descriptors.
 *
 * ISC-69: emits console.debug at the start of every call listing the sub_type
 * and the columns being searched.
 *
 * Match strategy:
 *  1. Canonical variant path: sub_type ∈ {"normal","valued"} + message_value
 *     → exact composite key lookup.
 *  2. Empty sub_type → grouped category key (exact match after legacy remap).
 *  3. Friendly name path → normalised label comparison against individual
 *     columns, then fallback to the grouped category column.
 */
export function matchLegacyEntryToColumn(
  entry: LegacyOffsetEntry,
  columns: AdapterColumnDescriptor[],
): LegacyMatchResult {
  const sub = entry.message_sub_type;

  devDebug("[legacySubTypeNormalizer] attempt", {
    message_type: entry.message_type,
    message_sub_type: sub,
    message_value: entry.message_value,
  });

  // Canonical variant path
  if ((sub === "normal" || sub === "valued") && typeof entry.message_value === "number") {
    const catKey = categoryKeyFromMcNumber(entry.message_type);
    if (catKey === "") return { columnKey: null, confidence: "none" };
    const compositeKey = `${catKey}:${sub}:${entry.message_value}`;
    const found = columns.find((c) => c.key === compositeKey);
    if (found) return { columnKey: compositeKey, confidence: "exact" };
    return { columnKey: null, confidence: "none" };
  }

  // Empty sub_type → grouped category
  if (!sub || sub === "") {
    const mcNum = remapLegacyMessageType(entry.message_type);
    const catKey = categoryKeyFromMcNumber(mcNum);
    if (catKey === "") return { columnKey: null, confidence: "none" };
    return { columnKey: catKey, confidence: "exact" };
  }

  // Friendly-name path
  const mcNum = remapLegacyMessageType(entry.message_type);
  const catKey = categoryKeyFromMcNumber(mcNum);
  if (catKey === "") return { columnKey: null, confidence: "none" };
  const normalizedTarget = normalizeSubType(sub);

  const individualMatch = columns.find((col) => {
    if (!col.isIndividual) return false;
    const labelParts = col.label.split(": ");
    if (labelParts.length < 2) return false;
    const variantLabel = labelParts.slice(1).join(": ");
    const normalizedCol = normalizeSubType(variantLabel);
    if (normalizedCol !== normalizedTarget) return false;
    return col.key.startsWith(`${catKey}:`) || col.key === catKey;
  });

  if (individualMatch) {
    devDebug("[legacySubTypeNormalizer] matched", {
      raw: sub,
      normalized: normalizedTarget,
      column: individualMatch.key,
    });
    return { columnKey: individualMatch.key, confidence: "normalized" };
  }

  const fallbackCat = columns.find((c) => c.key === catKey);
  if (fallbackCat) return { columnKey: catKey, confidence: "normalized" };
  return { columnKey: null, confidence: "none" };
}

/**
 * Filters `entries` down to those that could not be matched to any column.
 * Useful for building the unresolved-entries list before showing the Alert UI.
 */
export function collectUnresolvedLegacyEntries(
  entries: LegacyOffsetEntry[],
  columns: AdapterColumnDescriptor[],
): LegacyOffsetEntry[] {
  return entries.filter((e) => matchLegacyEntryToColumn(e, columns).confidence === "none");
}

/**
 * Deduplicates an array of unresolved legacy entries by (message_type,
 * message_sub_type), accumulating unit/offset ranges for each group.
 * Returns at most 20 groups (ISC-66 / Finding #16).
 */
export function dedupeUnresolvedEntries(entries: LegacyOffsetEntry[]): UnresolvedGroup[] {
  const groups = new Map<string, LegacyOffsetEntry[]>();
  entries.forEach((e) => {
    const key = `${e.message_type}|${e.message_sub_type ?? ""}`;
    const arr = groups.get(key) ?? [];
    arr.push(e);
    groups.set(key, arr);
  });
  const out: UnresolvedGroup[] = [];
  groups.forEach((arr) => {
    const units = arr.map((e) => e.unit_number).sort((a, b) => a - b);
    const offsets = arr.map((e) => e.offset).sort((a, b) => a - b);
    const unitMin = units[0];
    const unitMax = units[units.length - 1];
    const offsetMin = offsets[0];
    const offsetMax = offsets[offsets.length - 1];
    const unitSummary =
      unitMin === unitMax
        ? `unit ${unitMin}, offset ${offsetMin}`
        : `units ${unitMin}-${unitMax}, offsets ${offsetMin}..${offsetMax}`;
    out.push({
      message_type: arr[0].message_type,
      message_sub_type: arr[0].message_sub_type,
      unitSummary,
      count: arr.length,
      sampleOffsets: offsets.slice(0, 5),
    });
  });
  return out.slice(0, 20);
}

/**
 * Emits ONE aggregated console.warn for all unresolved legacy entries (ISC-A9).
 * Emits nothing when the array is empty.
 */
export function logUnresolvedBatch(unresolved: LegacyOffsetEntry[]): void {
  if (unresolved.length === 0) return;
  const sample = unresolved.slice(0, 5);
  devWarn(
    `[deserializeOffsetMapping] ${unresolved.length} legacy offset entries could not be routed to any column`,
    { totalCount: unresolved.length, sample },
  );
}
